import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { BarChart3, Columns3, History, Inbox, LayoutList, Receipt, Settings } from 'lucide-react';
import { Toaster } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ThemeProvider } from './components/ThemeProvider';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import { LogoutButton } from './components/LogoutButton';
import { DarkModeToggle } from './components/DarkModeToggle';
import { AuthGuard } from './components/AuthGuard';
import { QuoteFlow } from './components/QuoteFlow';
import { QuoteDrawer } from './components/QuoteDrawer';
import { KanbanBoard } from './components/KanbanBoard';
import { ContractorSettingsDrawer } from './components/ContractorSettingsDrawer';
import { AnalyticsView } from './components/AnalyticsView';
import { RequestsView } from './components/RequestsView';
import { InvoiceListView } from '@/components/InvoiceListView';
import { PageContainer } from '@/components/ui/page-container';
import { Footer } from './components/Footer';
import { TermsPage } from './pages/TermsPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { HelpPage } from './pages/HelpPage';
import { QuoteRequestPage } from './components/QuoteRequestPage';
import { SharedQuotePage } from './components/SharedQuotePage';
import { ConfirmDialog } from './components/ConfirmDialog';
import { useQuoteRequests } from './api/hooks';

function getHashRoute(): string {
  return globalThis.location.hash;
}

function loadQuoteView(): 'list' | 'board' {
  try {
    const stored = globalThis.localStorage.getItem('voicequote-quote-view');
    return stored === 'board' ? 'board' : 'list';
  } catch {
    return 'list';
  }
}

function renderSharePage(hash: string): React.ReactNode {
  const shareToken = hash.slice('#quote/'.length);
  return (
    <ThemeProvider>
      <SharedQuotePage token={shareToken} />
    </ThemeProvider>
  );
}

function renderRequestPage(hash: string): React.ReactNode {
  const requestPublicId = hash.slice('#request/'.length);
  return (
    <ThemeProvider>
      <div className="min-h-screen bg-background text-foreground">
        <QuoteRequestPage userId={requestPublicId} />
      </div>
    </ThemeProvider>
  );
}

const STATIC_PAGES = { terms: TermsPage, privacy: PrivacyPage, help: HelpPage } as const;

function renderStaticPage(page: keyof typeof STATIC_PAGES): React.ReactNode {
  const PageComponent = STATIC_PAGES[page];
  return (
    <ThemeProvider>
      <div className="min-h-screen bg-background text-foreground">
        <PageComponent />
      </div>
    </ThemeProvider>
  );
}

interface HeaderProps {
  isAuthenticated: boolean;
  isDesktop: boolean;
  showAnalytics: boolean;
  showRequests: boolean;
  showInvoices: boolean;
  quoteView: 'list' | 'board';
  requestsNewCount: number;
  onSettingsClick: () => void;
  onAnalyticsClick: () => void;
  onRequestsClick: () => void;
  onInvoicesClick: () => void;
  onToggleView: () => void;
  onDrawerToggle: () => void;
}

function AppHeader({
  isAuthenticated,
  isDesktop,
  showAnalytics,
  showRequests,
  showInvoices,
  quoteView,
  requestsNewCount,
  onSettingsClick,
  onAnalyticsClick,
  onRequestsClick,
  onInvoicesClick,
  onToggleView,
  onDrawerToggle,
}: Readonly<HeaderProps>) {
  const { t } = useTranslation();

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-card">
      <div className="mx-auto flex max-w-[28rem] items-center justify-between px-4 py-3 lg:max-w-6xl">
        <h1 className="text-lg font-bold text-primary">{t('app.title')}</h1>
        <div className="flex items-center gap-2">
          {isAuthenticated && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="touch-target"
                onClick={onSettingsClick}
                aria-label={t('contractor.settingsAriaLabel')}
                title={t('contractor.settingsAriaLabel')}
              >
                <Settings className="h-5 w-5" />
              </Button>
              <Button
                variant={showAnalytics ? 'default' : 'ghost'}
                size="icon"
                className="touch-target"
                onClick={onAnalyticsClick}
                aria-label={t('analytics.ariaLabel')}
                title={t('analytics.ariaLabel')}
                data-testid="analytics-tab-btn"
              >
                <BarChart3 className="h-5 w-5" />
              </Button>
              <Button
                variant={showRequests ? 'default' : 'ghost'}
                size="icon"
                className="touch-target relative"
                onClick={onRequestsClick}
                aria-label={t('requests.title')}
                title={t('requests.title')}
                data-testid="requests-tab-btn"
              >
                <Inbox className="h-5 w-5" />
                {requestsNewCount > 0 && (
                  <Badge
                    className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center bg-green-500 px-1 text-[10px] text-white"
                    data-testid="requests-new-badge"
                  >
                    {requestsNewCount}
                  </Badge>
                )}
              </Button>
              <Button
                variant={showInvoices ? 'default' : 'ghost'}
                size="icon"
                className="touch-target"
                onClick={onInvoicesClick}
                aria-label={t('invoice.ariaLabel')}
                title={t('invoice.ariaLabel')}
                data-testid="invoices-tab-btn"
              >
                <Receipt className="h-5 w-5" />
              </Button>
              {isDesktop
                ? !showAnalytics &&
                  !showRequests &&
                  !showInvoices && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="touch-target"
                      onClick={onToggleView}
                      aria-label={t(quoteView === 'list' ? 'quotes.viewBoard' : 'quotes.viewList')}
                      title={t(quoteView === 'list' ? 'quotes.viewBoard' : 'quotes.viewList')}
                    >
                      {quoteView === 'list' ? (
                        <Columns3 className="h-5 w-5" />
                      ) : (
                        <LayoutList className="h-5 w-5" />
                      )}
                    </Button>
                  )
                : !showAnalytics &&
                  !showRequests &&
                  !showInvoices && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="touch-target"
                      onClick={onDrawerToggle}
                      aria-label={t('quotes.title')}
                      title={t('quotes.title')}
                    >
                      <History className="h-5 w-5" />
                    </Button>
                  )}
            </>
          )}
          <LanguageSwitcher />
          <DarkModeToggle />
          {isAuthenticated && <LogoutButton />}
        </div>
      </div>
    </header>
  );
}

interface MainContentProps {
  showAnalytics: boolean;
  showRequests: boolean;
  showInvoices: boolean;
  isDesktop: boolean;
  quoteView: 'list' | 'board';
  selectedQuoteId: string | null;
  sidebarOpen: boolean;
  getAccessToken: () => Promise<string>;
  onQuoteLoaded: () => void;
  onActiveChange: (active: boolean) => void;
  onQuoteSelected: (quoteId: string) => void;
  onQuoteDeleted: (quoteId: string) => void;
  onSelectBoardQuote: (id: string) => void;
  onNewBoardQuote: () => void;
}

function MainContent({
  showAnalytics,
  showRequests,
  showInvoices,
  isDesktop,
  quoteView,
  selectedQuoteId,
  sidebarOpen,
  getAccessToken,
  onQuoteLoaded,
  onActiveChange,
  onQuoteSelected,
  onQuoteDeleted,
  onSelectBoardQuote,
  onNewBoardQuote,
}: Readonly<MainContentProps>) {
  if (showAnalytics) {
    return (
      <main className="flex-1">
        <AuthGuard>
          <AnalyticsView getAccessToken={getAccessToken} />
        </AuthGuard>
      </main>
    );
  }

  if (showRequests) {
    return (
      <main className="flex-1">
        <AuthGuard>
          <RequestsView getAccessToken={getAccessToken} />
        </AuthGuard>
      </main>
    );
  }

  if (showInvoices) {
    return (
      <main className="flex-1">
        <AuthGuard>
          <InvoiceListView />
        </AuthGuard>
      </main>
    );
  }

  const showBoardView = isDesktop && quoteView === 'board';

  if (showBoardView) {
    return <KanbanBoard onSelectQuote={onSelectBoardQuote} onNewQuote={onNewBoardQuote} />;
  }

  return (
    <>
      <main className="lg:flex-1">
        <PageContainer>
          <AuthGuard>
            <QuoteFlow
              selectedQuoteId={selectedQuoteId}
              onQuoteLoaded={onQuoteLoaded}
              onActiveChange={onActiveChange}
            />
          </AuthGuard>
        </PageContainer>
      </main>
      {sidebarOpen && (
        <QuoteDrawer
          isOpen={isDesktop}
          onClose={() => {}}
          onSelect={onQuoteSelected}
          onDelete={onQuoteDeleted}
          mode="sidebar"
        />
      )}
    </>
  );
}

export function App() {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, getAccessToken } = useKindeAuth();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const [drawerOpen, setDrawerOpen] = useState(isDesktop);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showRequests, setShowRequests] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showInvoices, setShowInvoices] = useState(() => getHashRoute() === '#invoices');
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null);
  const [hasActiveQuote, setHasActiveQuote] = useState(false);
  const [hash, setHash] = useState(getHashRoute);
  const [quoteView, setQuoteView] = useState(loadQuoteView);
  const [pendingLoadQuoteId, setPendingLoadQuoteId] = useState<string | null>(null);

  const { data: requestsData } = useQuoteRequests(
    getAccessToken as () => Promise<string>,
    undefined,
    1,
    !!isAuthenticated,
  );
  const requestsNewCount = isAuthenticated ? (requestsData?.pages[0]?.new_count ?? 0) : 0;

  useEffect(() => {
    const onHashChange = () => setHash(getHashRoute());
    globalThis.addEventListener('hashchange', onHashChange);
    return () => globalThis.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  const handleQuoteSelected = useCallback(
    (quoteId: string) => {
      if (hasActiveQuote) {
        setPendingLoadQuoteId(quoteId);
        return;
      }
      setSelectedQuoteId(quoteId);
      if (!isDesktop) {
        setDrawerOpen(false);
      }
    },
    [hasActiveQuote, isDesktop],
  );

  const handleLoadConfirmed = useCallback(() => {
    /* istanbul ignore else -- @preserve: pendingLoadQuoteId is always set when dialog is open */
    if (pendingLoadQuoteId) {
      setSelectedQuoteId(pendingLoadQuoteId);
      setPendingLoadQuoteId(null);
      if (!isDesktop) {
        setDrawerOpen(false);
      }
    }
  }, [pendingLoadQuoteId, isDesktop]);

  const handleQuoteLoaded = useCallback(() => {
    setSelectedQuoteId(null);
  }, []);

  const handleActiveChange = useCallback((active: boolean) => {
    setHasActiveQuote(active);
  }, []);

  const handleQuoteDeleted = useCallback(
    (quoteId: string) => {
      if (selectedQuoteId === quoteId) {
        setSelectedQuoteId(null);
        setHasActiveQuote(false);
      }
    },
    [selectedQuoteId],
  );

  const toggleView = useCallback(() => {
    setQuoteView((prev) => {
      const next = prev === 'list' ? 'board' : 'list';
      globalThis.localStorage.setItem('voicequote-quote-view', next);
      return next;
    });
  }, []);

  const sidebarOpen = !!isAuthenticated && isDesktop && quoteView === 'list';

  if (hash.startsWith('#quote/')) {
    return renderSharePage(hash);
  }

  if (hash.startsWith('#request/')) {
    return renderRequestPage(hash);
  }

  // eslint-disable-next-line security/detect-possible-timing-attacks -- URL hash routing, not secret comparison
  if (hash === '#terms') return renderStaticPage('terms');
  // eslint-disable-next-line security/detect-possible-timing-attacks
  if (hash === '#privacy') return renderStaticPage('privacy');
  // eslint-disable-next-line security/detect-possible-timing-attacks
  if (hash === '#help') return renderStaticPage('help');

  const handleAnalyticsClick = () => {
    setShowAnalytics((prev) => !prev);
    setShowRequests(false);
    setShowInvoices(false);
    setSettingsOpen(false);
  };

  const handleRequestsClick = () => {
    setShowRequests((prev) => !prev);
    setShowAnalytics(false);
    setShowInvoices(false);
    setSettingsOpen(false);
  };

  const handleInvoicesClick = () => {
    setShowInvoices((prev) => !prev);
    setShowAnalytics(false);
    setShowRequests(false);
    setSettingsOpen(false);
  };

  const handleSelectBoardQuote = (id: string) => {
    setQuoteView('list');
    globalThis.localStorage.setItem('voicequote-quote-view', 'list');
    setSelectedQuoteId(id);
    setDrawerOpen(true);
  };

  const handleNewBoardQuote = () => {
    setQuoteView('list');
    globalThis.localStorage.setItem('voicequote-quote-view', 'list');
  };

  return (
    <ThemeProvider>
      <Toaster richColors position="bottom-center" />
      <div className="flex min-h-screen flex-col overflow-x-hidden bg-background text-foreground">
        <AppHeader
          isAuthenticated={isAuthenticated}
          isDesktop={isDesktop}
          showAnalytics={showAnalytics}
          showRequests={showRequests}
          showInvoices={showInvoices}
          quoteView={quoteView}
          requestsNewCount={requestsNewCount}
          onSettingsClick={() => setSettingsOpen((prev) => !prev)}
          onAnalyticsClick={handleAnalyticsClick}
          onRequestsClick={handleRequestsClick}
          onInvoicesClick={handleInvoicesClick}
          onToggleView={toggleView}
          onDrawerToggle={() => setDrawerOpen((prev) => !prev)}
        />
        <div className="flex-1 lg:flex">
          <MainContent
            showAnalytics={showAnalytics}
            showRequests={showRequests}
            showInvoices={showInvoices}
            isDesktop={isDesktop}
            quoteView={quoteView}
            selectedQuoteId={selectedQuoteId}
            sidebarOpen={sidebarOpen}
            getAccessToken={getAccessToken as () => Promise<string>}
            onQuoteLoaded={handleQuoteLoaded}
            onActiveChange={handleActiveChange}
            onQuoteSelected={handleQuoteSelected}
            onQuoteDeleted={handleQuoteDeleted}
            onSelectBoardQuote={handleSelectBoardQuote}
            onNewBoardQuote={handleNewBoardQuote}
          />
        </div>
        <Footer />
      </div>
      {!isDesktop && drawerOpen && isAuthenticated && (
        <QuoteDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onSelect={handleQuoteSelected}
          onDelete={handleQuoteDeleted}
          mode="drawer"
        />
      )}
      <ContractorSettingsDrawer isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <ConfirmDialog
        open={pendingLoadQuoteId !== null}
        onOpenChange={
          /* istanbul ignore next -- @preserve: controlled component, open=true never emitted */ (
            open,
          ) => {
            if (!open) setPendingLoadQuoteId(null);
          }
        }
        title={t('quotes.confirmLoad')}
        description=""
        onConfirm={handleLoadConfirmed}
      />
    </ThemeProvider>
  );
}
